
import type { ReactNode } from 'react';
import { Layers, TriangleAlert } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  EmptyState as KitEmptyState,
  Skeleton,
} from '../../components/ui';
import { emptySearchReason, useInsertSearch, useInserts } from '../../hooks';
import type { ApiError } from '../../api/client';
import type { Id, Insert, InsertSearchResponse, LibraryFacets } from '../../types/api';
import {
  MAX_SEARCH_ITEMS,
  MAX_SHOWCASE_ITEMS,
  activeFilterCount,
  describeActiveFilters,
  toListParams,
  toSearchRequest,
  type LibraryFilters,
} from './filters';
import { InsertCard } from './InsertCard';
import styles from './LibraryPage.module.css';

export interface ResultsShellProps {
  filters: LibraryFilters;
  limit: number;
  onMore: () => void;
  renderToolbar: (found: number | null, loading: boolean) => ReactNode;
  panels: ReactNode;
  facets: LibraryFacets | undefined;
  selectedId: Id | null;
  onOpen: (item: Insert) => void;
  onReset: () => void;
  onEnrichClick: () => void;
  onUploadClick: () => void;
  canEdit: boolean;
}

export function ShowcaseResults(props: ResultsShellProps) {
  const { filters, limit } = props;
  const query = useInserts(toListParams(filters, limit));
  return (
    <ResultsBody
      {...props}
      items={query.data?.items ?? []}
      total={query.data?.total ?? null}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      error={query.error}
      onRetry={() => void query.refetch()}
      maxItems={MAX_SHOWCASE_ITEMS}
      diagnostics={null}
      emptyExtra={null}
    />
  );
}

export function SearchResults(props: ResultsShellProps) {
  const { filters, limit } = props;
  const payload = toSearchRequest(filters, Math.min(limit, MAX_SEARCH_ITEMS));
  const query = useInsertSearch(payload);
  const data = query.data;
  return (
    <ResultsBody
      {...props}
      items={data?.items ?? []}
      total={data?.total ?? null}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      error={query.error}
      onRetry={() => void query.refetch()}
      maxItems={MAX_SEARCH_ITEMS}
      diagnostics={<SearchDiagnostics data={data} />}
      emptyExtra={emptySearchReason(data)}
    />
  );
}

interface BodyProps extends ResultsShellProps {
  items: Insert[];
  total: number | null;
  isLoading: boolean;
  isFetching: boolean;
  error: ApiError | null;
  onRetry: () => void;
  maxItems: number;
  diagnostics: ReactNode;
  emptyExtra: string | null;
}

function ResultsBody({
  filters,
  limit,
  onMore,
  renderToolbar,
  panels,
  facets,
  selectedId,
  onOpen,
  onReset,
  onEnrichClick,
  onUploadClick,
  canEdit,
  items,
  total,
  isLoading,
  isFetching,
  error,
  onRetry,
  maxItems,
  diagnostics,
  emptyExtra,
}: BodyProps) {
  const untagged = facets ? facets.total - facets.tagged : 0;
  const canLoadMore = total !== null && items.length < total && limit < maxItems;
  const cappedByLimit = total !== null && items.length < total && limit >= maxItems;

  return (
    <>
      {renderToolbar(total, isLoading || isFetching)}
      {panels}
      {diagnostics}

      {error && <ErrorBanner error={error} onRetry={onRetry} />}

      {!error && isLoading && <GridSkeleton />}

      {!error && !isLoading && items.length === 0 && (
        <EmptyState
          filters={filters}
          facets={facets}
          extraReason={emptyExtra}
          untagged={untagged}
          onReset={onReset}
          onUploadClick={onUploadClick}
          canEdit={canEdit}
        />
      )}

      {!error && items.length > 0 && (
        <>
          <div className={`${styles.grid} ${isFetching ? styles.gridStale : ''}`}>
            {items.map((item) => (
              <InsertCard
                key={item.id}
                item={item}
                selected={item.id === selectedId}
                onOpen={onOpen}
              />
            ))}
          </div>

          <div className={styles.gridFooter}>
            <span className={styles.footerCount}>
              Показано {items.length}
              {total !== null ? ` из ${total}` : ''}
            </span>
            {canLoadMore && (
              <Button size="sm" onClick={onMore} disabled={isFetching}>
                {isFetching ? 'Загружаем…' : 'Показать ещё'}
              </Button>
            )}
            {cappedByLimit && (
              <span className={styles.footerNote}>
                Показаны первые {maxItems}. Сузьте фильтры.
              </span>
            )}

            {untagged > 0 && (
              <span className={styles.footerWarn}>
                <TriangleAlert size={13} strokeWidth={1.5} />
                {untagged}{' '}
                {plural(untagged, 'материал ждёт', 'материала ждут', 'материалов ждут')}{' '}
                разметки
              </span>
            )}
            {untagged > 0 && canEdit && (
              <Button size="sm" onClick={onEnrichClick}>
                Разметить
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );
}

function GridSkeleton() {
  return (
    <div className={styles.grid} aria-busy="true">
      {Array.from({ length: 8 }, (_, index) => (
        <div key={index} className={styles.skeletonCard}>
          <div className={styles.skeletonFrame} />
          <div className={styles.skeletonLines}>
            <Skeleton width="100%" height={9} />
            <Skeleton width="55%" height={9} />
          </div>
        </div>
      ))}
    </div>
  );
}

interface EmptyProps {
  filters: LibraryFilters;
  facets: LibraryFacets | undefined;
  extraReason: string | null;
  untagged: number;
  onReset: () => void;
  onUploadClick: () => void;
  canEdit: boolean;
}

function EmptyState({
  filters,
  facets,
  extraReason,
  untagged,
  onReset,
  onUploadClick,
  canEdit,
}: EmptyProps) {
  const query = filters.q.trim();
  const filterCount = activeFilterCount(filters);

  if (facets && facets.total === 0) {
    return (
      <div className={styles.emptyWrap}>
        <KitEmptyState
          icon={<Layers size={18} strokeWidth={1.4} />}
          title="В библиотеке пока пусто"
          description="Загрузите видео и фото: теги проставятся сами."
          action={
            canEdit ? (
              <Button variant="primary" onClick={onUploadClick}>
                Загрузить материалы
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  }

  const reasons: string[] = [];
  if (extraReason) reasons.push(extraReason);
  if (query) reasons.push(`запрос «${query}» ни с чем не совпал`);
  if (filterCount > 0) reasons.push(`фильтры: ${describeActiveFilters(filters)}`);
  if (query && untagged > 0) {
    reasons.push(
      `${untagged} ${plural(untagged, 'материал', 'материала', 'материалов')} ещё не размечены`,
    );
  }

  return (
    <div className={styles.emptyWrap}>
      <KitEmptyState
        icon={<Layers size={18} strokeWidth={1.4} />}
        title="Ничего не нашлось"
        description={
          reasons.length > 0 ? (
            <ul className={styles.emptyReasons}>
              {reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : undefined
        }
        action={
          filterCount > 0 || query ? (
            <Button onClick={onReset}>Сбросить фильтры и поиск</Button>
          ) : undefined
        }
      />
    </div>
  );
}

function SearchDiagnostics({ data }: { data: InsertSearchResponse | undefined }) {
  if (!data) return null;
  const notes: string[] = [];
  if (data.degraded) {
    notes.push(data.degraded_reason || 'Поиск сработал не полностью, выдача неполная');
  }
  if (data.filters_applied_after_ranking) {
    notes.push('Счётчик найденного может быть занижен');
  }
  if (notes.length === 0) return null;

  return (
    <div className={styles.diagnostics} role="status">
      <TriangleAlert size={13} strokeWidth={1.6} className={styles.diagnosticsIcon} />
      <div>
        {notes.map((note) => (
          <div key={note}>{note}</div>
        ))}
      </div>
    </div>
  );
}

function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = count % 100;
  const mod10 = count % 10;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}
